import { useMemo, useState } from 'react';
import { PortfolioItem, Item } from '../../lib/api';
import { formatPrice } from '../../lib/i18n';

interface PortfolioMiniCardProps {
  item: PortfolioItem | Item;
  onClick?: () => void;
  className?: string;
}

const buildFallbackCover = (title?: string | null) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(title || 'Splyt')}&size=512&background=1a1f2e&color=00FF88`;

export function PortfolioMiniCard({ item, onClick, className = '' }: PortfolioMiniCardProps) {
  const [coverFailed, setCoverFailed] = useState(false);

  // Check if item is an Item (marketplace item) or PortfolioItem
  const isMarketplaceItem = 'id' in item && 'creator_id' in item;

  const coverUrl = useMemo(() => {
    if (coverFailed) return buildFallbackCover(item.title);
    
    // For marketplace Item
    if (isMarketplaceItem) {
      const marketItem = item as Item;
      return marketItem.cover_url || buildFallbackCover(marketItem.title);
    }
    
    // For PortfolioItem
    const portfolioItem = item as PortfolioItem;
    return (
      portfolioItem.cover_url ||
      portfolioItem.preview_url ||
      portfolioItem.value ||
      portfolioItem.link ||
      buildFallbackCover(portfolioItem.title)
    );
  }, [coverFailed, item, isMarketplaceItem]);

  const label = useMemo(() => {
    if (item.category && typeof item.category === 'string') {
      return item.category;
    }
    // Check for type property on PortfolioItem
    if ('type' in item && item.type) {
      return item.type;
    }
    return 'Portfolio';
  }, [item]);

  const rawPrice =
    typeof item.price_uah === 'number' && !Number.isNaN(item.price_uah) && item.price_uah >= 0
      ? item.price_uah
      : null;

  const formattedPrice =
    rawPrice !== null
      ? rawPrice === 0
        ? 'Безкоштовно'
        : formatPrice(rawPrice / 100)
      : '';

  return (
    <div
      className={`relative aspect-square overflow-hidden rounded-2xl border border-white/10 bg-[#0B1120] cursor-pointer ${className}`}
      onClick={onClick}
    >
      <img
        src={coverUrl}
        alt={item.title || 'Portfolio item'}
        className="w-full h-full object-cover"
        loading="lazy"
        onError={() => setCoverFailed(true)}
      />

      {/* Gradient overlay for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

      {/* Category / type badge */}
      {label && (
        <div className="absolute top-2 left-2">
          <span className="px-2.5 py-1 rounded-lg text-[10px] font-medium glass backdrop-blur-xl text-white/90 border border-white/20">
            {label}
          </span>
        </div>
      )}

      {/* Title */}
      {item.title && (
        <div className="absolute left-2 right-2 bottom-6">
          <p className="text-[11px] font-semibold text-white line-clamp-2 drop-shadow-sm">
            {item.title}
          </p>
        </div>
      )}

      {/* Price pill */}
      {formattedPrice && (
        <div className="absolute left-2 bottom-2">
          <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-black/60 text-splyt-green border border-splyt-green/40">
            {formattedPrice}
          </span>
        </div>
      )}
    </div>
  );
}
